import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { UserMinus, Users, Building2, Loader2, AlertTriangle, User } from "lucide-react";
import { toast } from "sonner";
import { bulkUpdateLeads } from "../../services/crmServices/leadService";
import { useAuth } from "../../hooks/useAuth";

export function BulkUnassignModal({ open, onClose, selectedLeads, onSuccess }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percentage: 0 });

    const [unassignFields, setUnassignFields] = useState({
        agent: false,
        desk: false,
        floorManager: false
    });

    const role = user?.position?.role;
    const canUnassignFromDesk = role === "admin" || role === "floorManager";
    const canUnassignFromFloorManager = role === "admin";

    const selectedCount = Object.values(unassignFields).filter(Boolean).length;

    const toggleField = (field) => {
        setUnassignFields((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleUnassign = async () => {
        if (selectedCount === 0) {
            toast.error("Please select at least one field to unassign");
            return;
        }

        setLoading(true);
        const startTime = Date.now();
        const totalLeads = selectedLeads.length;
        setProgress({ current: 0, total: totalLeads, percentage: 0 });

        const parts = [];
        if (unassignFields.agent) parts.push("user");
        if (unassignFields.desk) parts.push("desk");
        if (unassignFields.floorManager) parts.push("floor manager");
        const targetText = parts.join(", ");

        const progressToast = toast.loading(`Preparing to unassign ${totalLeads.toLocaleString()} leads from ${targetText}...`);

        try {
            const leadIds = selectedLeads.map((lead) => lead._id);

            const updates = {};
            if (unassignFields.agent) updates.agent = null;
            if (unassignFields.desk) updates.desk = null;
            if (unassignFields.floorManager) updates.floorManager = null;

            const unassignmentData = { leadIds, updates };

            let progressStep = 0;
            const progressInterval = setInterval(() => {
                setProgress(prev => {
                    progressStep++;
                    const progressValue = Math.min(95, Math.round(100 * (1 - Math.exp(-progressStep / 8))));
                    const currentCount = Math.round((progressValue / 100) * totalLeads);
                    const elapsed = Math.round((Date.now() - startTime) / 1000);
                    const estimated = progressValue > 5 ? Math.round((elapsed / progressValue) * 100) : 0;
                    const remaining = Math.max(0, estimated - elapsed);
                    
                    toast.loading(
                        `Unassigning ${currentCount.toLocaleString()} / ${totalLeads.toLocaleString()} leads (${progressValue}%) • ${remaining}s remaining`,
                        { id: progressToast }
                    );
                    return { current: currentCount, total: totalLeads, percentage: progressValue };
                });
            }, 300);

            const response = await bulkUpdateLeads(unassignmentData);

            clearInterval(progressInterval);
            const elapsed = Math.round((Date.now() - startTime) / 1000);
            toast.dismiss(progressToast);
            setProgress({ current: totalLeads, total: totalLeads, percentage: 100 });

            if (response?.status === 200) {
                const actualCount = response?.data?.count || selectedLeads.length;
                const timeText = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
                toast.success(`✓ Successfully unassigned ${actualCount.toLocaleString()} leads from ${targetText} in ${timeText}`, { duration: 5000 });
                onSuccess();
                handleClose();
            } else {
                toast.error("Failed to unassign leads");
            }
        } catch (error) {
            toast.dismiss(progressToast);
            console.error("Error unassigning leads:", error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to unassign leads";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setProgress({ current: 0, total: 0, percentage: 0 });
        }
    };

    const handleClose = () => {
        setUnassignFields({ agent: false, desk: false, floorManager: false });
        onClose();
    };

    const options = [
        {
            key: "floorManager",
            label: "Floor Manager",
            description: "Remove floor manager assignment",
            icon: Users,
            color: "red",
            visible: canUnassignFromFloorManager
        },
        {
            key: "desk",
            label: "Desk",
            description: "Remove desk assignment",
            icon: Building2,
            color: "rose",
            visible: canUnassignFromDesk
        },
        {
            key: "agent",
            label: "User",
            description: "Remove user assignment",
            icon: User,
            color: "orange",
            visible: true
        }
    ];

    const visibleOptions = options.filter((o) => o.visible);

    const colorStyles = {
        orange: {
            active: "border-orange-300 bg-orange-50/60 shadow-sm",
            icon: "bg-orange-100 text-orange-600",
            checkbox: "text-orange-600 border-orange-300 accent-orange-600"
        },
        rose: {
            active: "border-rose-300 bg-rose-50/60 shadow-sm",
            icon: "bg-rose-100 text-rose-600",
            checkbox: "text-rose-600 border-rose-300 accent-rose-600"
        },
        red: {
            active: "border-red-300 bg-red-50/60 shadow-sm",
            icon: "bg-red-100 text-red-600",
            checkbox: "text-red-600 border-red-300 accent-red-600"
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col p-0 gap-0 rounded-xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 bg-gradient-to-r from-slate-50 to-white border-b">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-100 text-orange-600">
                            <UserMinus className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                            <DialogHeader className="p-0 space-y-0.5">
                                <DialogTitle className="text-lg font-semibold text-gray-900">
                                    Unassign Leads
                                </DialogTitle>
                                <DialogDescription className="text-sm text-gray-500">
                                    Select which assignments to remove from {selectedLeads.length} lead{selectedLeads.length !== 1 ? "s" : ""}
                                </DialogDescription>
                            </DialogHeader>
                        </div>
                        <Badge variant="secondary" className="text-xs font-medium">
                            {selectedLeads.length} selected
                        </Badge>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {/* Unassign Options - Checkboxes */}
                    <div className="space-y-2.5">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Unassign From</p>
                        <div className="space-y-2.5">
                            {visibleOptions.map((option) => {
                                const Icon = option.icon;
                                const isActive = unassignFields[option.key];
                                const colors = colorStyles[option.color];

                                return (
                                    <div
                                        key={option.key}
                                        onClick={() => toggleField(option.key)}
                                        className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                                            isActive
                                                ? colors.active
                                                : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                                                isActive ? colors.icon : "bg-gray-100 text-gray-400"
                                            }`}>
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium ${isActive ? "text-gray-900" : "text-gray-700"}`}>
                                                    {option.label}
                                                </p>
                                                <p className="text-xs text-gray-500">{option.description}</p>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={() => {}}
                                                className={`h-4.5 w-4.5 rounded border-gray-300 cursor-pointer ${colors.checkbox}`}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Summary of what will happen */}
                    {selectedCount > 0 && !loading && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-4">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">What will happen</p>
                            <ul className="space-y-1.5">
                                {unassignFields.floorManager && (
                                    <li className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                                        Floor manager will be removed
                                    </li>
                                )}
                                {unassignFields.desk && (
                                    <li className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className="w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                                        Desk assignment will be removed
                                    </li>
                                )}
                                {unassignFields.agent && (
                                    <li className="flex items-center gap-2 text-sm text-gray-700">
                                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
                                        User assignment will be removed
                                    </li>
                                )}
                                {/* Show what remains */}
                                {(!unassignFields.floorManager || !unassignFields.desk || !unassignFields.agent) && (
                                    <li className="flex items-center gap-2 text-xs text-gray-400 mt-1 pt-1 border-t border-gray-200">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                                        Remaining: {[
                                            !unassignFields.floorManager && canUnassignFromFloorManager && "floor manager",
                                            !unassignFields.desk && canUnassignFromDesk && "desk",
                                            !unassignFields.agent && "user"
                                        ].filter(Boolean).join(", ") || "none"}
                                    </li>
                                )}
                            </ul>
                        </div>
                    )}

                    {/* Progress Bar */}
                    {loading && progress.total > 0 && (
                        <div className="rounded-lg border border-orange-200 bg-orange-50/70 p-4">
                            <div className="flex items-center justify-between mb-2.5">
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
                                    <span className="text-sm font-medium text-orange-800">Unassigning leads...</span>
                                </div>
                                <span className="text-sm font-semibold text-orange-700">{progress.percentage}%</span>
                            </div>
                            <div className="w-full bg-orange-200/60 rounded-full h-2 overflow-hidden">
                                <div
                                    className="bg-orange-600 h-2 rounded-full transition-all duration-500 ease-out"
                                    style={{ width: `${progress.percentage}%` }}
                                />
                            </div>
                            <p className="text-xs text-orange-600 mt-1.5">
                                {progress.current.toLocaleString()} / {progress.total.toLocaleString()} leads processed
                            </p>
                        </div>
                    )}

                    {/* Warning */}
                    {selectedCount > 0 && (
                        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50/70 border border-red-200">
                            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-red-600 leading-relaxed">
                                This action cannot be undone. Leads will need to be manually reassigned after unassignment.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-gray-50/80 flex items-center gap-3">
                    <Button
                        onClick={handleUnassign}
                        disabled={loading || selectedCount === 0}
                        className="flex-1 h-10 text-sm font-medium rounded-lg shadow-sm bg-orange-600 hover:bg-orange-700 text-white"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Unassigning...
                            </>
                        ) : (
                            <>
                                <UserMinus className="h-4 w-4 mr-2" />
                                Unassign {selectedLeads.length} Leads
                            </>
                        )}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={loading}
                        className="h-10 px-5 text-sm font-medium rounded-lg"
                    >
                        Cancel
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
